import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import {
    changeOperatorChatText,
    changeOperatorMessageContentType,
} from "src/app/chats-panel/~store/actions/actions";
import TagSearchingInput from "src/app/shared/components/tag-searching-input/TagSearchingInput";
import {
    ISearchingTag,
    TagName,
    TagType,
} from "src/app/shared/components/tag-searching-input/~types/models/SearchingTag";
import { IApplicationState } from "src/~store/models/ApplicationState";
import {
    getTopicPairCategoriesByTopicName,
    getTopicPairsByTopicNameWithFilter,
    saveTopicPairUsedTag,
    logOperatorAnswer,
} from "../../~store/actions/actions";
import {
    isGettingTopicPairsSelector,
    selectedTopicPairStatusSelector,
    topicPairCategoriesSelector,
    topicPairSourcesSelector,
    topicSearchSelector,
} from "../../~store/selectors";
import { ITopicItemSearchProps } from "../topic-item/~types/TopicItemSearchProps";
import { getNlpSearchingTags, TOPIC_PAIRS_ON_PAGE } from "./../../~store/actions/constants";
import { useTranslation } from "react-i18next";

import "./TopicItemSearch.css";
import { ITopicPair } from "../../~store/models/TopicPair";
import { operatorCurrentChatTextSelector, operatorStatusSelector } from "src/app/chats-panel/~store/selectors";
import {
    TopicPairContentType,
    topicPairContentTypeToString,
} from "../../~store/models/enums/TopicPairContentType";
import { ConvertTextToContent } from "src/app/shared/components/text-to-content/TextToContent";
import Paging from "src/app/shared/components/paging/Paging";
import { OperatorStatusType } from "../../../chats-panel/~store/state/ChatItemsState";
import { showErrorToastr } from "../../../shared/helpers/toastr-helper/ToastrHelper";
import { NLP_SEARCH_TOPIC_PATH } from "src/app/AppConstants";

const BUTTON_VISIBILITY_THRESHOLD = 110;

type ReduxType = ReturnType<typeof mapStateToProps> & ITopicItemSearchProps;

const TopicItemSearch = (props: ReduxType) => {
    const { t } = useTranslation();
    const [topicName, setTopicName] = useState<string>();
    const [currentTags, setCurrentTags] = useState<ISearchingTag[]>();
    const [selectedPage, setSelectedPage] = React.useState<number>(1);
    const tableRef = useRef<HTMLDivElement | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const [topicNotFound, setTopicNotFound] = useState(false);

    useEffect(() => {
        getTopicName();
    }, []);

    useEffect(() => {
        getTopicName();
        if ((location.state as any)?.theme.length > 0) {
            const openingTag: ISearchingTag = {
                id: 0,
                value: t("common.phrase"),
                tagType: TagType.OpeningTag,
                tagName: TagName.Phrase,
            };
            const operationTag: ISearchingTag = {
                id: 1,
                value: "=",
                tagType: TagType.OperationTag,
            };
            const closingTag: ISearchingTag = {
                id: 2,
                value: (location.state as any)?.theme,
                tagType: TagType.ClosingTag,
                tagName: TagName.Phrase,
            };

            const tags: ISearchingTag[] = [openingTag, operationTag, closingTag];

            setCurrentTags(tags);
        } else {
            setCurrentTags([]);
        }
    }, [(location.state as any)?.time]);

    useEffect(() => {
        if (topicNotFound) return;
        if (topicName && !props.isGettingTopicPairs) {
            props.getTopicPairCategoriesByTopicName(topicName, () => setTopicNotFound(true));
        }
    }, [topicName]);

    useEffect(() => {
        if (topicNotFound) return;
        if (topicName && !props.isGettingTopicPairs)
            props.getTopicPairsByTopicNameWithFilter(
                topicName,
                currentTags || [],
                selectedPage,
                () => setTopicNotFound(true)
            );
    }, [topicName, currentTags, selectedPage]);

    useEffect(() => {
        if (!topicNotFound) return;
        showErrorToastr(t("error.nlpTopicNotFoundError", { topicName: topicName }));
        navigate("/nlu");
    }, [topicNotFound]);

    useLayoutEffect(() => {
        const items = tableRef.current?.querySelectorAll(
            ".topic-pairs-search-table__item"
        );
        items?.forEach((item) => {
            const button = item.querySelector<HTMLDivElement>(
                ".topic-pairs-search-table__item-collapse-button"
            );
            const answerContainer = item.querySelector<HTMLDivElement>(
                ".topic-pairs-search-answer-text"
            );
            const questionContainer = item.querySelector<HTMLDivElement>(
                ".topic-pairs-search-question"
            );

            if (answerContainer && button && questionContainer) {
                answerContainer.style.height = "";

                if (answerContainer.scrollHeight <= Math.max(questionContainer.offsetHeight, BUTTON_VISIBILITY_THRESHOLD)) {
                    answerContainer.style.height = "auto"; 
                    button.style.display = "none";
                } else {
                    button.style.display = "block";
                }
            }
        });
    });

    const toggleShowAnswerText = (id: number) => {
        const container = document.querySelector<HTMLDivElement>(
            `div[data-topic-pairs-item-id="${id}"]`
        );
        if (container) {
            const text = container.querySelector<HTMLDivElement>(
                ".topic-pairs-search-answer-text"
            );
            const button = container.querySelector<HTMLDivElement>(
                ".topic-pairs-search-table__item-collapse-button"
            );
            text?.classList.toggle("opened");
            button?.classList.toggle("opened");
        }
    };

    const getTopicName = () => {
        let encodedTopicname = window.location.href.split(`${NLP_SEARCH_TOPIC_PATH}/`)[1];    
        let decodedTopicname = decodeURIComponent(encodedTopicname)
        setTopicName(decodedTopicname);
    };

    const _onSendButtonClick = (topicPair: ITopicPair) => {
        if (
            topicPair.answer?.text &&
            props.currentMessageText != topicPair.answer.text
        ) {
            props.changeOperatorChatText(topicPair.answer.text);
            props.changeOperatorMessageContentType(topicPair.contentType);
            props.saveTopicPairUsedTag(topicPair);
            props.logOperatorAnswer(topicPair, topicName, currentTags);
        }
    };

    const _mapTopicPairsTable = () => {
        return (
            <div className="topic-pairs-search-table">
                <div className="topic-pairs-search-table__header">
                    {/* <div /> */}
                    {/* <div>{t("chat.subject")}</div>
          <div>{t("nlp.theme")}</div> */}
                    <div>{t("nlp.question")}</div>
                    <div>{t("nlp.answer")}</div>
                    <div />
                </div>
                <div className="topic-pairs-search-table__body" ref={tableRef}>
                    {props.topicSearch.topicPairs?.map((item) => {
                        return (
                            <div
                                className="topic-pairs-search-table__item"
                                key={item.topicPairId}
                                data-topic-pairs-item-id={item?.topicPairId}
                            >
                                <div className="topic-pairs-search-table__item_info">
                                    <div className="topic-pairs-search-id tag">
                                        {item.topicPairId ? `#${item.topicPairId}` :
                                            `#${t("common.ai")}`} 
                                    </div>
                                    {item.source && (
                                        <div className="topic-pairs-search-source tag">
                                            {item.source}
                                        </div>
                                    )}
                                    {item.contentType && (
                                        <div className="topic-pairs-search-content-type tag">
                                            {topicPairContentTypeToString(item.contentType)}
                                        </div>
                                    )}
                                    {item.subject && (
                                        <div className="topic-pairs-search-category tag">
                                            {item.subject}
                                        </div>
                                    )}
                                </div>
                                <div className="topic-pairs-search-table__item-main">
                                    <div className="topic-pairs-search-question">
                                        <div>
                                            <b>{item.questions.find((q) => q.isDefault)?.text}</b>
                                        </div>
                                        <ul className="topic-pairs-search-question__list">
                                            {item.questions
                                                .filter((q) => !q.isDefault)
                                                .map((q, i) => {
                                                    return <li key={i}>{q.text}</li>;
                                                })}
                                        </ul>
                                    </div>
                                    <div className="topic-pairs-search-answer-text">
                                        {ConvertTextToContent(
                                            item.contentType,
                                            item.answer?.text ?? "",
                                            false
                                        )}
                                    </div>
                                    <div className="topic-pairs-search-button">
                                        <Button
                                            onClick={() => {
                                                if (props.operatorStatus === OperatorStatusType.Offline) {
                                                    showErrorToastr(t('nlp.errorSendNLPSearchWhenOperatorOffline'))
                                                }
                                                else {
                                                    _onSendButtonClick(item);
                                                }
                                            }}
                                        >
                                            {t("common.send")}
                                        </Button>
                                    </div>
                                </div>
                                <div
                                    className="topic-pairs-search-table__item-collapse-button"
                                    onClick={() => toggleShowAnswerText(item.topicPairId)}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const _mapFilter = () => {
        return (
            <div className="topic-pairs-search-input">
                <TagSearchingInput
                    searchingTags={getNlpSearchingTags(
                        props.topicPairCategories,
                        props.topicPairSources
                    )}
                    onSearch={_onSearchClicked}
                    topicExternalId={props.topicSearch.topicExternalId}
                    topicPairStatus={props.topicPairStatus}
                    repeatingTags={false}
                    currentTags={currentTags || []}
                    onClearFilter={() => setCurrentTags([])}
                />
            </div>
        );
    };

    const _onSearchClicked = (tags: ISearchingTag[]) => {
        setCurrentTags(tags);
    };

    return (
        <div className="topic-search-container main-section">
            <div>
                <div className="topic-search-header">{topicName}</div>
                {_mapFilter()}
                <Paging
                    countOfElements={props.topicSearch.topicPairsCount}
                    countOnPages={TOPIC_PAIRS_ON_PAGE}
                    onTileClick={(sp) => {
            if(topicName) {
                            setSelectedPage(sp);
                            props.getTopicPairsByTopicNameWithFilter(
                                topicName,
                                currentTags || [],
                                sp
                            );
                        }
                    }}
                    selectedPage={selectedPage}
                />
            </div>
            {_mapTopicPairsTable()}
        </div>
    );
};

const mapStateToProps = (state: IApplicationState) => {
    return {
        topicSearch: topicSearchSelector(state),
        topicPairCategories: topicPairCategoriesSelector(state),
        topicPairSources: topicPairSourcesSelector(state),
        topicPairStatus: selectedTopicPairStatusSelector(state),
        currentMessageText: operatorCurrentChatTextSelector(state),
        isGettingTopicPairs: isGettingTopicPairsSelector(state),
    operatorStatus: operatorStatusSelector(state)
    };
};

const mapDispatchToProps = (dispatch: any): ITopicItemSearchProps => {
    return {
        getTopicPairsByTopicNameWithFilter: (
            topicName: string,
            tags: ISearchingTag[],
            selectedPage: number,
            onError?: () => void
        ) =>
            dispatch(
                getTopicPairsByTopicNameWithFilter(
                    topicName,
                    tags,
                    selectedPage,
                    onError
                )
            ),
        getTopicPairCategoriesByTopicName: (topicName: string, onError?: () => void) =>
            dispatch(getTopicPairCategoriesByTopicName(topicName, onError)),
        changeOperatorChatText: (text: string) =>
            dispatch(changeOperatorChatText(text)),
        changeOperatorMessageContentType: (contentType: TopicPairContentType) => {
            return dispatch(changeOperatorMessageContentType(contentType));
        },
        saveTopicPairUsedTag: (topicPair: ITopicPair) =>
            dispatch(saveTopicPairUsedTag(topicPair)),
        logOperatorAnswer: (topicPair: ITopicPair, topicName: string | undefined, currentTags: ISearchingTag[] | undefined) =>
            dispatch(logOperatorAnswer(topicPair, topicName, currentTags))           
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TopicItemSearch);
